import React, { useState } from 'react';
/**
 * Project Detail - Fullscreen Storytelling View
 * Three-phase narrative: Intentie → Proces → Resultaat
 */
import { motion } from 'framer-motion';
import { ArrowLeft, Info, CheckCircle2, Lightbulb, Target, Wrench, Trophy, Play } from 'lucide-react';
import { useTheme } from '../../../core/contexts/ThemeContext';
import { Project } from './data/projects';
interface ProjectDetailProps {
  project: Project;
  onBack: () => void;
  onInfoClick: () => void;
}
export function ProjectDetail({
  project,
  onBack,
  onInfoClick
}: ProjectDetailProps) {
  const {
    getThemeClasses,
    theme: currentTheme
  } = useTheme();
  const theme = getThemeClasses();
  const Icon = project.icon;
  const [activePhase, setActivePhase] = useState<'intentie' | 'proces' | 'resultaat'>('intentie');
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  const phases = [{
    id: 'intentie' as const,
    label: 'Intentie',
    icon: Target,
    color: 'from-purple-500 to-violet-500'
  }, {
    id: 'proces' as const,
    label: 'Proces',
    icon: Wrench,
    color: 'from-fuchsia-500 to-pink-500'
  }, {
    id: 'resultaat' as const,
    label: 'Resultaat',
    icon: Trophy,
    color: 'from-emerald-500 to-teal-500'
  }];
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className={`h-full flex flex-col ${theme.bg} overflow-hidden`} data-id="element-388">
      {/* Header - Mobile Optimized */}
      <div className={`px-3 md:px-6 py-3 md:py-4 ${theme.card} border-b flex items-center justify-between ${isLightTheme ? 'border-gray-200' : isHighContrast ? 'border-black' : isNeonTheme ? 'border-fuchsia-500/50' : 'border-white/10'}`} data-id="element-389">
        <button onClick={onBack} className={`flex items-center gap-1 md:gap-2 transition-colors touch-manipulation ${isLightTheme ? 'text-gray-600 hover:text-gray-900' : isHighContrast ? 'text-black hover:text-gray-700' : isNeonTheme ? 'text-cyan-300 hover:text-cyan-100' : 'text-white/60 hover:text-white'}`} data-id="element-390">
          <ArrowLeft className="w-4 h-4" data-id="element-391" />
          <span className="text-xs md:text-sm" data-id="element-392">Terug</span>
        </button>
        <div className="flex items-center gap-2 md:gap-3" data-id="element-393">
          <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg bg-gradient-to-br ${project.gradient} flex items-center justify-center ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-394">
            <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" data-id="element-395" />
          </div>
          <div className="hidden sm:block" data-id="element-396">
            <div className={`text-xs md:text-sm font-bold ${theme.text}`} data-id="element-397">
              {project.title}
            </div>
            <div className={`text-xs hidden md:block ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/40'}`} data-id="element-398">
              {project.subtitle}
            </div>
          </div>
        </div>
        <button onClick={onInfoClick} className={`p-2 rounded-lg transition-colors touch-manipulation ${isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : isHighContrast ? 'bg-gray-100 hover:bg-gray-200' : isNeonTheme ? 'bg-fuchsia-900/20 hover:bg-fuchsia-900/30 border border-fuchsia-500/30' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-399">
          <Info className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-400" />
        </button>
      </div>
      {/* Hero Media Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="relative" data-id="element-401">
        {project.videoUrl ? <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black/50" data-id="element-402">
            <iframe src={project.videoUrl} className="w-full h-full" allow="autoplay" title={project.title} data-id="element-403" />
          </div> : <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 relative group" data-id="element-404">
            <img src={project.heroImage} alt={project.title} className="w-full h-full object-cover" data-id="element-405" />
            <motion.div initial={{
          opacity: 0
        }} whileHover={{
          opacity: 1
        }} className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer" data-id="element-406">
              <Play className="w-16 h-16 text-white" data-id="element-407" />
            </motion.div>
          </div>}
      </motion.div>
      {/* Phase Selector - Horizontal Scroll on Mobile */}
      <div className={`px-3 md:px-8 py-3 md:py-4 border-b overflow-x-auto ${isLightTheme ? 'bg-gray-100 border-gray-200' : isHighContrast ? 'bg-gray-100 border-black' : isNeonTheme ? 'bg-black border-fuchsia-500/30' : 'bg-black/20 border-white/10'}`} data-id="element-408">
        <div className="flex items-center gap-2 min-w-max md:max-w-5xl md:mx-auto" data-id="element-409">
          {phases.map(phase => {
          const PhaseIcon = phase.icon;
          const isActive = activePhase === phase.id;
          return <button key={phase.id} onClick={() => setActivePhase(phase.id)} className={`relative flex items-center gap-2 px-3 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all whitespace-nowrap touch-manipulation ${isActive ? 'text-white' : isLightTheme ? 'text-gray-600 hover:text-gray-900' : isHighContrast ? 'text-black hover:text-gray-700' : isNeonTheme ? 'text-cyan-300 hover:text-cyan-100' : 'text-white/50 hover:text-white/80'}`} data-id="element-410">
                {isActive && <motion.div layoutId="activePhase" className={`absolute inset-0 rounded-lg ${isHighContrast ? 'bg-black' : isNeonTheme ? `bg-gradient-to-r ${phase.color} shadow-lg shadow-fuchsia-500/50` : `bg-gradient-to-r ${phase.color}`}`} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-411" />}
                <div className="relative z-10 flex items-center gap-2" data-id="element-412">
                  <PhaseIcon className="w-3 h-3 md:w-4 md:h-4" data-id="element-413" />
                  <span className="text-xs md:text-base" data-id="element-414">{phase.label}</span>
                </div>
              </button>;
        })}
        </div>
      </div>
      {/* Content - Mobile Optimized Spacing */}
      <div className="flex-1 overflow-y-auto p-3 md:p-8" data-id="element-415">
        <motion.div key={activePhase} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="max-w-5xl mx-auto space-y-4 md:space-y-6" data-id="element-416">
          {activePhase === 'intentie' && <div className="space-y-4 md:space-y-6" data-id="element-417">
              <div className={`backdrop-blur-xl rounded-xl md:rounded-2xl p-4 md:p-8 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-418">
                <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4" data-id="element-419">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center flex-shrink-0 ${isNeonTheme ? 'shadow-lg shadow-purple-500/50' : ''}`} data-id="element-420">
                    <Target className="w-5 h-5 md:w-6 md:h-6 text-white" data-id="element-421" />
                  </div>
                  <div className="flex-1" data-id="element-422">
                    <h2 className={`text-xl md:text-2xl font-bold mb-2 ${theme.text}`} data-id="element-423">
                      {project.story.intentie.title}
                    </h2>
                    <p className={`leading-relaxed text-sm md:text-lg ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/70'}`} data-id="element-424">
                      {project.story.intentie.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>}
          {activePhase === 'proces' && <div className="space-y-4 md:space-y-6" data-id="element-425">
              <div className={`backdrop-blur-xl rounded-xl md:rounded-2xl p-4 md:p-8 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-426">
                <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6" data-id="element-427">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center flex-shrink-0 ${isNeonTheme ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-428">
                    <Wrench className="w-5 h-5 md:w-6 md:h-6 text-white" data-id="element-429" />
                  </div>
                  <div className="flex-1" data-id="element-430">
                    <h2 className={`text-xl md:text-2xl font-bold mb-2 ${theme.text}`} data-id="element-431">
                      {project.story.proces.title}
                    </h2>
                    <p className={`leading-relaxed text-sm md:text-lg ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/70'}`} data-id="element-432">
                      {project.story.proces.description}
                    </p>
                  </div>
                </div>
                {project.story.proces.tools && <div className={`pt-4 md:pt-6 border-t ${isLightTheme ? 'border-gray-200' : isHighContrast ? 'border-black' : isNeonTheme ? 'border-fuchsia-500/30' : 'border-white/10'}`} data-id="element-433">
                    <h3 className={`text-xs md:text-sm font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-434">
                      Tools & Technologieën
                    </h3>
                    <div className="flex gap-2 flex-wrap" data-id="element-435">
                      {project.story.proces.tools.map(tool => <div key={tool} className={`px-2 md:px-3 py-1.5 md:py-2 rounded-lg border text-xs md:text-sm ${isLightTheme ? 'bg-gray-50 border-gray-200 text-gray-700' : isHighContrast ? 'bg-gray-100 border-black text-black' : isNeonTheme ? 'bg-fuchsia-900/20 border-cyan-500/30 text-cyan-300' : 'bg-white/5 border-white/10 text-white/80'}`} data-id="element-436">
                          {tool}
                        </div>)}
                    </div>
                  </div>}
              </div>
            </div>}
          {activePhase === 'resultaat' && <div className="space-y-4 md:space-y-6" data-id="element-437">
              <div className={`backdrop-blur-xl rounded-xl md:rounded-2xl p-4 md:p-8 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-438">
                <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6" data-id="element-439">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 ${isNeonTheme ? 'shadow-lg shadow-emerald-500/50' : ''}`} data-id="element-440">
                    <Trophy className="w-5 h-5 md:w-6 md:h-6 text-white" data-id="element-441" />
                  </div>
                  <div className="flex-1" data-id="element-442">
                    <h2 className={`text-xl md:text-2xl font-bold mb-2 ${theme.text}`} data-id="element-443">
                      {project.story.resultaat.title}
                    </h2>
                    <p className={`leading-relaxed text-sm md:text-lg ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/70'}`} data-id="element-444">
                      {project.story.resultaat.description}
                    </p>
                  </div>
                </div>
                {project.story.resultaat.metrics && <div className={`pt-4 md:pt-6 border-t ${isLightTheme ? 'border-gray-200' : isHighContrast ? 'border-black' : isNeonTheme ? 'border-fuchsia-500/30' : 'border-white/10'}`} data-id="element-445">
                    <h3 className={`text-xs md:text-sm font-bold uppercase mb-4 ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-446">
                      Key Metrics
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-id="element-447">
                      {project.story.resultaat.metrics.map(metric => <div key={metric} className={`p-3 md:p-4 rounded-lg border ${isLightTheme ? 'bg-emerald-50 border-emerald-200' : isHighContrast ? 'bg-gray-100 border-black' : isNeonTheme ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-emerald-500/10 border-emerald-500/20'}`} data-id="element-448">
                          <div className="flex items-center gap-2 mb-1" data-id="element-449">
                            <CheckCircle2 className={`w-3 h-3 md:w-4 md:h-4 ${isHighContrast ? 'text-black' : 'text-emerald-400'}`} data-id="element-450" />
                            <span className={`text-xs font-medium ${isHighContrast ? 'text-black' : 'text-emerald-400'}`} data-id="element-451">
                              Success
                            </span>
                          </div>
                          <div className={`text-sm md:text-base font-bold ${theme.text}`} data-id="element-452">
                            {metric}
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </div>
            </div>}
        </motion.div>
      </div>
    </motion.div>;
}